import styled from '@emotion/styled'
import { withClass } from 'common/emotion'
import { WebLink } from 'common/navigation/web-link'

import { colors } from 'component-library/__styling/settings'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { supports } from 'component-library/__helpers/at-supports-helper'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'

export const StyledWrapper = styled('div')(
    {
        fontWeight: 400,
        padding: `${calcRem(36)} 0 ${calcRem(43)} 0`,
        // grid fallback
        display: 'flex',
        flexWrap: 'wrap',
    }, // the grid is designed to have a masonary layout at smaller sizes
    ({ theme }) =>
        supports('display: grid', {
            display: 'grid',

            gridTemplateColumns: 'max-content',
            gridTemplateRows: 'max-content',
            gridGap: '30px 8vw',

            [breakpoint('xxs')]: {
                gridGap: '30px 3vw',
            },

            [breakpoint('xs')]: {
                gridTemplateColumns: '1fr 1fr',
            },

            // This just switches to a 3 col on regional sites, due to overlapping items.
            [breakpoint('sm')]: {
                gridTemplateColumns:
                    theme.section !== 'default' ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', // required on regionals to prevent overlapping
            },

            // Goes back to a 4 col for all types of sites at 900px - which is basically at an optimal point
            ['@media (min-width: 900px)']: {
                gridTemplateColumns: 'repeat(4, 1fr)',
            },
        }),
    ({ theme }) => ({
        fontFamily: theme.fonts.sansSerif,
    })
)

export const StyledList = styled('div')(
    {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
    }, // The max height is used creates the wrapping effect in the first column.
    // otherwise the grid would just render the content to it's full height.
    // This is wrapped in a grid supports because ie11 can't render column layouts well
    supports('display: grid', {
        [breakpoint('xxs')]: {
            maxHeight: 260,
        },

        [breakpoint('xs')]: {
            maxHeight: 'none',
        },

        [breakpoint('sm')]: {
            maxHeight: 280,
        },

        [breakpoint('md')]: {
            maxHeight: 260,
        },
    })
)

const styledLinkBlockClassName = 'westFooter__styledLinkBlock'

export const StyledLinkBlock = withClass(styledLinkBlockClassName)(
    styled('div')({
        marginBottom: 4,

        '&:last-child': {
            marginBottom: 0,
        },
    })
)

export const StyledBlock = styled('div')(
    ({ theme }) => ({
        margin: '30px 3vw',

        '&:first-child': {
            minWidth: 380,
        },

        ...conditionalStyles(theme.section !== 'default', {
            '& span:last-of-type': {
                marginBottom: calcRem(10),
                marginTop: calcRem(25),
            },
        }),

        // first two blocks span the full height of the rows
    }),
    supports('display: grid', {
        margin: 0, // remove grid fallback fix

        // The grid changes at every breakpoint to try and better arange itself to each size,
        // there is a lot of re-aranging to get a kind of masonary layout on
        // smaller mobile sizes.
        '&:first-child': {
            minWidth: 0,
            gridColumn: 'auto / span 2',

            // might be an emotion issue, this isnt playing nice - seems to work
            [`&. ${styledLinkBlockClassName}`]: {
                width: '50%',
            },
        },

        '&:last-child': {
            gridColumn: 'auto / span 2',
        },

        [breakpoint('xxs')]: {
            '&:last-child': {
                gridColumn: 'auto',
            },
        },

        [breakpoint('xs')]: {
            '&:first-child': {
                gridColumn: 'auto',
                gridRow: 'auto / span 3',
            },
        },

        [breakpoint('sm')]: {
            // first two children are full height of the container
            '&:first-child': {
                gridColumn: 'auto / span 2',
                gridRow: 'auto',
            },

            // first two children are full height of the container
            '&:nth-child(-n+2)': {
                gridRow: 'auto / span 2',
            },
        },
    })
)

export const StyledBlockHeader = styled('h2')(({ theme }) => ({
    fontWeight: 600,
    fontSize: calcRem(18),
    lineHeight: 1,
    color: colors.black,
    textTransform: 'uppercase',
    margin: `0 0 ${calcRem(16)} 0`,

    '&:before': {
        content: `''`,
        display: 'inline-block',
        verticalAlign: 'middle',
        transform: `translateY(${calcRem(-1)})`,
        height: calcRem(16),
        width: calcRem(3),
        marginRight: 6,
        backgroundColor: theme.sectionValues.primaryColor,
    },
}))

export const StyledText = styled('span')(({ theme }) => ({
    display: 'block',
    fontWeight: 400,
    fontSize: calcRem(16),
    marginBottom: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    color: colors.thewest.greyElephant,
}))

export const StyledTitle = styled(StyledText.withComponent('h3'))({
    fontWeight: 700,
    marginBottom: calcRem(5),
})

export const StyledLink = styled(WebLink)(({ theme }) => ({
    color: colors.thewest.greyElephant,
    display: 'inline-block',
    textDecoration: 'none',
    fontFamily: theme.fonts.sansSerif,

    '&:focus': {
        outline: 'none',
        textDecoration: 'underline',
        color: colors.thewest.greyGorilla,
    },

    '&:hover': {
        color: theme.sectionValues.primaryColor,
    },
}))
